import 'draft-js/dist/Draft.css'
import '@kaidam/fc-shared/assets/texteditor/shared.css'
import '@kaidam/fc-shared/assets/texteditor/editor.css'
import React, { FC, useState, useMemo, useCallback, useEffect, useRef, KeyboardEvent, MouseEvent } from 'react'
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box'

import {
  Editor,
  EditorState,
  convertToRaw,
  convertFromRaw,
  RawDraftContentState,
  RichUtils,
  getDefaultKeyBinding,
  Modifier,
  DraftHandleValue,
  ContentState,
} from 'draft-js'
import { docsSoap } from 'docs-soap'
import { convertFromHTML } from 'draft-convert'
import decorator from './decorators'
import EditorContext from './EditorContext'

import {
  getNumber,
} from '@kaidam/fc-shared/src/utils/number'

import {
  ITextEditor_RawUpdate,
  ITextEditor_HorizontalAlign,
  ITextEditor_Options,
} from '@kaidam/fc-shared/src/types/texteditor'

import {
  blockClassnameFn,
  handleReturnFactory,
  getColorStyles,
  moveSelectionToEnd,
} from './utils'

import {
  TEXT_EDITOR_HORIZONTAL_ALIGN_DEFAULT,
  TEXT_EDITOR_ENTITY_TYPE_LINK,
  TEXT_EDITOR_LINK_TARGET_BLANK,
  TEXT_EDITOR_LINK_TYPE_TEXT,
} from '@kaidam/fc-shared/src/constants'

import Toolbar from './Toolbar'

interface StyleProps {
  horizontalAlign?: ITextEditor_HorizontalAlign,
  padding?: number,
}

const useStyles = makeStyles(theme => ({
  editor: ({horizontalAlign, padding}: StyleProps) => ({
    flexGrow: 1,
    overflowY: 'auto',
    border: '1px solid #ccc',
    padding: theme.spacing(getNumber(padding)),
    textAlign: horizontalAlign,
    cursor: 'text',
    minHeight: '60vh',
  }),
}))

interface TextEditorProps {
  value: RawDraftContentState | null,
  options?: ITextEditor_Options,
  onChange: ITextEditor_RawUpdate,
}

const TextEditor: FC<React.PropsWithChildren<TextEditorProps>> = ({
  value,
  options = {},
  onChange,
}) => {

  const {
    horizontalAlign = TEXT_EDITOR_HORIZONTAL_ALIGN_DEFAULT,
    padding = 1,
    showButtons,
  } = options

  const classes = useStyles({horizontalAlign, padding})
  const editorRef = useRef<Editor | null>(null)

  const [ editorState, setEditorState ] = useState(() =>
    EditorState.createEmpty(decorator),
  )

  const onChangeHandler = useCallback((state: EditorState) => {
    setEditorState(state)
    onChange(convertToRaw(state.getCurrentContent()))
  }, [])

  const handleKeyCommand = useCallback((command: string, editorState: EditorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command)
    if (newState) {
      onChangeHandler(newState)
      return 'handled'
    }
    return 'not-handled'
  }, [])

  const keyBindingFn = useCallback((e: any) => {
    if (e.key.toLowerCase() === 'tab') {
      e.preventDefault()
      e.stopPropagation()
      const newEditorState = RichUtils.onTab(
        e,
        editorState,
        4, /* maxDepth */
      )
      if (newEditorState !== editorState) {
        onChangeHandler(newEditorState)
      }
      return null
    }
    return getDefaultKeyBinding(e)
  }, [
    editorState,
  ])

  const onFocus = useCallback(() => {
    if(!editorRef.current) return
    editorRef.current.focus()
  }, [
    editorRef.current,
  ])

  const onFocusEnd = useCallback(() => {
    if(!editorRef.current) return
    editorRef.current.focus()
    const newEditorState = moveSelectionToEnd(editorState)
    setEditorState(newEditorState)
  }, [
    editorRef.current,
    editorState
  ])

  // TODO: handle word HTML and the other edge cases that crop up because of pasting HTML
  // from external editors - the draft-convert module is good at handling these things
  const onPasteText = useCallback((text: string, html: string, editorState: EditorState): DraftHandleValue => {
    if(!html) return 'not-handled'
    const cleanHTML: string = docsSoap(html)
    const htmlContentState = convertFromHTML({
      htmlToEntity: (nodeName, node, createEntity) => {
        if (nodeName === 'a') {
          return createEntity(
            TEXT_EDITOR_ENTITY_TYPE_LINK,
            'MUTABLE',
            {
              type: TEXT_EDITOR_LINK_TYPE_TEXT,
              target: TEXT_EDITOR_LINK_TARGET_BLANK,
              url: convertToRaw(ContentState.createFromText(node.getAttribute('href'))),
            }
          )
        }
        return undefined
      },
    })(cleanHTML)
    const updatedContentState = Modifier.replaceWithFragment(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      ContentState.createFromBlockArray(htmlContentState.getBlocksAsArray(), htmlContentState.getEntityMap()).getBlockMap(),
    )
    setEditorState(EditorState.createWithContent(updatedContentState))
    return 'handled'
  }, [])

  const style = useMemo<string>(() => {
    const contentState = editorState.getCurrentContent()
    return getColorStyles(contentState)
  }, [
    editorState,
  ])

  useEffect(() => {
    if(!value) return
    setEditorState(EditorState.createWithContent(convertFromRaw(value), decorator))
  }, [])

  return (
    <EditorContext.Provider value={{
      focus: onFocus,
      disableLinkClicks: true,
    }}>
      <style dangerouslySetInnerHTML={{__html: style}} />
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #F1F1F1',
        }}
      >
        {
          showButtons && Object.keys(showButtons).length == 0 ? null : (
            <Box
              className='toolbar'
              sx={{
                flexGrow: 0,
              }}
            >
              <Toolbar
                editorState={ editorState }
                options={ options }
                setEditorState={ setEditorState }
              />
            </Box>
          )
        }
        <Box className={ classes.editor } onClick={ onFocusEnd }>
          <Box
            onClick={(e: MouseEvent) => {
              // prevent this click triggering the container
              // (which would always force the selection to the end)
              e.stopPropagation()
              e.preventDefault()
              return false
            }}
          >
            <Editor
              ref={ editorRef }
              editorState={ editorState }
              blockStyleFn={ blockClassnameFn }
              handleKeyCommand={ handleKeyCommand }
              handleReturn={ handleReturnFactory(setEditorState) }
              handlePastedText={ onPasteText }
              keyBindingFn={ keyBindingFn }
              onChange={ onChangeHandler }
            />
          </Box>
        </Box>
      </Box>
    </EditorContext.Provider>
    
  )
}

export default TextEditor